import { useReducer, useRef } from 'react';
import constate from 'constate';
import assign from 'lodash/assign';
import merge from 'lodash/merge';
import createReducer from 'helpers/reducers-helper';
import { expireDateFormatter, panFormatter } from 'helpers/credit-card-helper';
import { isCreditCardCVC, isCreditCardExpireDate, isCreditCardPan } from 'helpers/rule-validation';
import { useMochiPayFlowContext } from 'feature/mochi-pay-flow/use-mochi-pay-flow';

export const CHANGE_PAN = 'change_pan';
export const CHANGE_EXPIRE_DATE = 'change_expire_date';
export const CHANGE_CVC = 'change_cvc';
export const PAN_ERROR = 'pan_error';
export const EXPIRE_DATE_ERROR = 'expire_date_error';
export const CVC_ERROR = 'cvc_error';
export const CHANGE_LOADING = 'change_loading';
export const APPEND_PAYMENT_DATA = 'append_payment_data';
export const SHOW_LOADING_COVER = 'show_loading_cover';

const useCreditCard = () => {
  const bindCardFormButtonRef = useRef();

  const { initWallet } = useMochiPayFlowContext();
  const initState = {
    pan: '',
    expireDate: '',
    cvc: '',
    loading: false,
    panError: '',
    expireDateError: '',
    cvcError: '',
    merchantUid: '',
    transactionId: '',
    loadingCover: false,
  };

  const changePan = (state, payload) => {
    return assign({}, state, { pan: panFormatter(payload) });
  };
  const changeExpireDate = (state, payload) => {
    return assign({}, state, { expireDate: expireDateFormatter(payload) });
  };
  const changeCVC = (state, payload) => {
    return assign({}, state, { cvc: payload });
  };

  const panError = (state, payload) => assign({}, state, { panError: payload });
  const expireDateError = (state, payload) => assign({}, state, { expireDateError: payload });
  const cvcError = (state, payload) => assign({}, state, { cvcError: payload });
  const appendPaymentData = (state, payload) =>
    assign({}, state, { merchantUid: payload.merchantUid, transactionId: payload.transactionId });
  const changeLoading = (state, payload) => merge({}, state, { loading: payload });
  const showLoadingCover = (state, payload) => merge({}, state, { loadingCover: payload });

  const reducer = createReducer(
    {
      [CHANGE_PAN]: changePan,
      [PAN_ERROR]: panError,
      [CHANGE_EXPIRE_DATE]: changeExpireDate,
      [EXPIRE_DATE_ERROR]: expireDateError,
      [CHANGE_CVC]: changeCVC,
      [CVC_ERROR]: cvcError,
      [CHANGE_LOADING]: changeLoading,
      [APPEND_PAYMENT_DATA]: appendPaymentData,
      [SHOW_LOADING_COVER]: showLoadingCover,
    },
    initState,
    true
  );
  const [state, dispatch] = useReducer(reducer, initState);

  const bindCard = () => {
    const bindCardFromCheck = () => {
      const isCcNumber = isCreditCardPan(state.pan);
      const isCcExpireDate = isCreditCardExpireDate(state.expireDate);
      const isCcCVC = isCreditCardCVC(state.cvc);

      const ccNumberErrorMessage = isCcNumber ? '' : '格式錯誤';
      const ccExpireDateErrorMessage = isCcExpireDate ? '' : '格式錯誤';
      const ccCVCErrorMessage = isCcCVC ? '' : '格式錯誤';

      dispatch({ type: PAN_ERROR, payload: ccNumberErrorMessage });
      dispatch({ type: EXPIRE_DATE_ERROR, payload: ccExpireDateErrorMessage });
      dispatch({ type: CVC_ERROR, payload: ccCVCErrorMessage });

      return isCcNumber && isCcExpireDate && isCcCVC;
    };

    if (!bindCardFromCheck()) return;

    dispatch({ type: CHANGE_LOADING, payload: true });

    initWallet(value => {
      dispatch({ type: APPEND_PAYMENT_DATA, payload: value });
      if (bindCardFormButtonRef.current !== null) {
        bindCardFormButtonRef.current.click();
      }
    });
  };

  return {
    state,
    dispatch,
    bindCard,
    bindCardFormButtonRef,
  };
};

const [CreditCardProvider, useCreditCardContext] = constate(useCreditCard);

export { CreditCardProvider, useCreditCardContext };
